import { EncryptSSIDResponse, DecodedSSID } from '../types/doctorTypes';
import { getErrorMessage } from '../utils/errorMessage';

// Use REACT_APP_API_BASE_URL from .env file
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL ?? process.env.API_BASE_URL ?? '').replace(/\/$/, '');

if (!API_BASE_URL) {
  console.error('API_BASE_URL is not set in .env file');
}

export const authService = {
  async encryptSSID(encodedSSID: string): Promise<EncryptSSIDResponse> {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL is not configured. Please set API_BASE_URL in your .env file');
    }

    const apiUrl = `${API_BASE_URL}/api/v1/doctor360-dashboard/encrypt-ssid`;
    
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          encoded_ssid: encodedSSID,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to validate SSID: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error: unknown) {
      console.error('Fetch error:', getErrorMessage(error));
      throw error instanceof Error ? error : new Error(getErrorMessage(error));
    }
  },

  extractSSIDFromURL(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const ssid = urlParams.get('SSID');
    return ssid;
  },

  decodeSSID(encodedSSID: string): DecodedSSID | null {
    try {
      const decoded = atob(encodedSSID);
      const parsed = JSON.parse(decoded);
      return parsed;
    } catch (error: unknown) {
      console.error('Failed to decode SSID:', getErrorMessage(error));
      return null;
    }
  },

  async validateAndAuthenticate(): Promise<boolean> {
    const ssid = this.extractSSIDFromURL();
    
    if (!ssid) {
      console.error('No SSID found in URL');
      return false;
    }

    if (!API_BASE_URL) {
      console.error('API_BASE_URL is not configured');
      return false;
    }

    try {
      const response = await this.encryptSSID(ssid);
      
      if (response.Message === 'SUCCESS' && response.Status === 1) {
        return true;
      }
      
      return false;
    } catch (error: unknown) {
      console.error('SSID validation error:', getErrorMessage(error));
      return false;
    }
  },

  async getIsManager(): Promise<number> {
    const ssid = this.extractSSIDFromURL();
    if (!ssid) {
      return 0;
    }

    try {
      const response = await this.encryptSSID(ssid);
      if (response.Message === 'SUCCESS' && response.Status === 1 && response.List && response.List.length > 0) {
        return response.List[0].is_manager ?? 0;
      }
      return 0;
    } catch (error: unknown) {
      console.error('Failed to get is_manager:', getErrorMessage(error));
      return 0;
    }
  }
};
